import { Data, Modal, Print, UI, Utils } from 'common';

import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import propTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import CertifyReprintModal from '../reprint/CertifyReprintModal.component';
import ReprintModal from '../reprint/ReprintModal.component';
import { PrintStatus } from '../shared/StatusOptions';
import HistoryUtil from '../utils/Util';
import AddressesPanel from './AddressesPanel.component';
import CustomsDetailsPanel from './CustomsDetailsPanel.component';
import DetailsLoader from './DetailsLoader.component';
import GlobalAdvantageProgramCoverage from './GlobalAdvantageProgramCoverage.component';
import ParcelGuardCoveragePanel from './ParcelGuardCoveragePanel.component';
import ReferencePanel from './ReferencePanel.component';
import RefundPanel from './RefundPanel.component';
import ServicesPanel from './ServicesPanel.component';
import TrackingDetails from './TrackingDetails.component';

const { WrappedAccordion, WrappedAccordionTabTemplate } = UI;
const { Util } = Utils;
const { ModalContext } = Modal;
const { Carrier } = Data;
const { PrintEngine } = Print;
const { Session } = Utils;

const SingleTransactionDetails = ({
	selectedPrint,
	labelAndOrderDetails,
	features,
	onClickCreateReturnLabelButton,
	onEmailTrackingInfoClick,
	costCodeOptions,
	setPrintsCostCode,
	onClickManageCostCodes,
	onFileClaimClick,
	onCheckClaimStatusClick,
	showUpgradeModal,
}) => {
	const [accordionActiveIndex, setAccordionActiveIndex] = useState([]);
	const [labelDataForPrint, setLabelDataForPrint] = useState({});
	const { showModal, showError } = useContext(ModalContext);
	const { printDetails } = isObject(selectedPrint) ? selectedPrint : {};
	const {
		toAddress,
		fromAddress,
		returnAddress,
		printTypeId,
		insuredValue,
		insuranceType,
		printStatus,
		carrier,
	} = printDetails;
	const showAddressesPanel =
		printTypeId !== 5 && isObject(toAddress) && isObject(returnAddress);
	const showParcelGuardCoveragePanel = HistoryUtil.showCheckClaimStatus(
		insuredValue,
		insuranceType
	);
	const { labelDetails = {} } = labelAndOrderDetails;
	const { amount } = labelDetails;

	const showRefundDetailPanel = () =>
		!isEmpty(labelAndOrderDetails) &&
		(printStatus === PrintStatus.RefundPending ||
			printStatus === PrintStatus.Refunded ||
			printStatus === PrintStatus.PartialRefundPending);

	const resetPrint = () => {
		setLabelDataForPrint({});
	};

	const onReprintClick = () => {
		const { IsInWinClient: isInWinClient } = Session.getSettings();
		// Use the Win Client's UI to reprint if running embedded in the Win Client
		if (isInWinClient) {
			// 64-bit ids (e.g. refunds[].universalId) are parsed as BigInt, which JSON.stringify can't serialize
			const labelDetailsJson = JSON.stringify(labelDetails, (_key, value) =>
				typeof value === 'bigint' ? value.toString() : value
			);
			// Promise.resolve throws if ReprintFromHistory is not defined (older versions of the Win Client)
			Promise.resolve(window.external.ReprintFromHistory(labelDetailsJson))
				.then()
				.catch(e =>
					showError({
						data: {
							handleType: 'custom',
							title: 'Unable to Reprint',
							description:
								'<p>Please upgrade to the latest Classic Stamps.com Desktop application in order to reprint this label.</p>',
						},
					})
				);
			return;
		}

		if (carrier === Carrier.USPS) {
			return showModal({
				component: CertifyReprintModal,
				data: {
					selectedPrint,
					labelDetails,
					setLabelDataForPrint,
				},
			});
		} else {
			showModal({
				component: ReprintModal,
				data: {
					selectedPrint,
					labelDetails,
					setLabelDataForPrint,
				},
			});
		}
	};

	useEffect(() => {
		// load user preferences and restored the collapsed/expanded state for each accordion
		setAccordionActiveIndex([0, 1]);
	}, []);

	const onClickCreateReturnLabel = () => {
		onClickCreateReturnLabelButton('historyPageDetailsPanel');
	};

	return (
		<div className="history-single-transaction-details">
			<TrackingDetails
				selectedPrint={selectedPrint}
				features={features}
				onClickCreateReturnLabelButton={onClickCreateReturnLabel}
				onFileClaimClick={onFileClaimClick}
				onCheckClaimStatusClick={onCheckClaimStatusClick}
				onEmailTrackingInfoClick={onEmailTrackingInfoClick}
				onReprintClick={onReprintClick}
			/>

			{showRefundDetailPanel() && (
				<WrappedAccordion
					activeIndex={accordionActiveIndex}
					multiple
					dataqa="transaction-details-refund-details"
				>
					<WrappedAccordionTabTemplate>
						<div className="accordion-header history-accordion-header">
							<span>Refund Details</span>
						</div>
						<div className="history-accordion-container">
							<RefundPanel
								printDetails={printDetails}
								labelAndOrderDetails={labelAndOrderDetails}
							/>
						</div>
					</WrappedAccordionTabTemplate>
				</WrappedAccordion>
			)}

			{showAddressesPanel && (
				<WrappedAccordion
					activeIndex={accordionActiveIndex}
					multiple
					dataqa="transaction-details-addresses"
				>
					<WrappedAccordionTabTemplate>
						<div className="accordion-header history-accordion-header">
							<span>Addresses</span>
						</div>
						<div className="history-accordion-container">
							<AddressesPanel
								toAddress={toAddress}
								fromAddress={fromAddress}
								returnAddress={returnAddress}
							/>
						</div>
					</WrappedAccordionTabTemplate>
				</WrappedAccordion>
			)}

			<WrappedAccordion
				activeIndex={accordionActiveIndex}
				multiple
				dataqa="transaction-details-services"
			>
				<WrappedAccordionTabTemplate>
					<div className="accordion-header history-accordion-header">
						<span>Services</span>
						{!isEmpty(labelAndOrderDetails) && (
							<span className="amount-display">
								{Util.formatCurrency(amount)}
							</span>
						)}
					</div>
					<div className="history-accordion-container service-accordion">
						{isEmpty(labelAndOrderDetails) ? (
							<DetailsLoader rows={7} showHeader />
						) : (
							<ServicesPanel
								labelAndOrderDetails={labelAndOrderDetails}
								printDetails={printDetails}
								features={features.fileClaim}
								onFileClaimClick={onFileClaimClick}
								showUpgradeModal={showUpgradeModal}
							/>
						)}
					</div>
				</WrappedAccordionTabTemplate>
			</WrappedAccordion>

			<WrappedAccordion
				activeIndex={accordionActiveIndex}
				multiple
				dataqa="transaction-details-reference"
			>
				<WrappedAccordionTabTemplate>
					<div className="accordion-header history-accordion-header">
						<span>Reference</span>
					</div>
					<div className="history-accordion-container">
						{isEmpty(labelAndOrderDetails) ? (
							<DetailsLoader rows={3} leftColumnWidth={150} />
						) : (
							<ReferencePanel
								labelAndOrderDetails={labelAndOrderDetails}
								costCodeOptions={costCodeOptions}
								printDetails={printDetails}
								setPrintsCostCode={setPrintsCostCode}
								onClickManageCostCodes={onClickManageCostCodes}
							/>
						)}
					</div>
				</WrappedAccordionTabTemplate>
			</WrappedAccordion>

			{!isEmpty(labelAndOrderDetails?.labelDetails?.customs?.customsItems) && (
				<WrappedAccordion
					className="customer-detail-accordion"
					activeIndex={accordionActiveIndex}
					multiple
					dataqa="transaction-details-customs-information"
				>
					<WrappedAccordionTabTemplate>
						<div className="accordion-header history-accordion-header">
							<i className="icon customs-icon" />
							<span>Customs Information</span>
						</div>
						<div className="history-accordion-container">
							<CustomsDetailsPanel
								customs={labelAndOrderDetails?.labelDetails?.customs}
							/>
						</div>
					</WrappedAccordionTabTemplate>
				</WrappedAccordion>
			)}

			{labelAndOrderDetails?.labelDetails?.gapClaimEligibility &&
				labelAndOrderDetails?.labelDetails?.gapClaimMaxAmount > 0 && (
					<WrappedAccordion
						activeIndex={accordionActiveIndex}
						multiple
						dataqa="transaction-details-gap-coverage"
					>
						<WrappedAccordionTabTemplate>
							<div className="accordion-header history-accordion-header">
								<i className="icon gap-coverage-icon" />
								<span>Global Advantage Program Coverage</span>
							</div>
							<div className="history-accordion-container">
								<GlobalAdvantageProgramCoverage
									labelDetails={labelAndOrderDetails?.labelDetails}
								/>
							</div>
						</WrappedAccordionTabTemplate>
					</WrappedAccordion>
				)}

			{showParcelGuardCoveragePanel && (
				<WrappedAccordion
					activeIndex={accordionActiveIndex}
					multiple
					dataqa="transaction-details-parcelguard-coverage"
				>
					<WrappedAccordionTabTemplate>
						<div className="accordion-header history-accordion-header">
							<i className="icon parcelguard-icon" />
							<span>ParcelGuard&trade; Coverage</span>
						</div>
						<div className="history-accordion-container">
							<ParcelGuardCoveragePanel
								printDetails={printDetails}
								onCheckClaimStatusClick={onCheckClaimStatusClick}
							/>
						</div>
					</WrappedAccordionTabTemplate>
				</WrappedAccordion>
			)}
			{!isEmpty(labelDataForPrint) && (
				<PrintEngine
					isReprint
					labelData={labelDataForPrint}
					resetPrint={resetPrint}
				/>
			)}
		</div>
	);
};

SingleTransactionDetails.propTypes = {
	selectedPrint: propTypes.object.isRequired,
	features: propTypes.object,
};

export default SingleTransactionDetails;
